import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Copy, Gift, Send, X, ChevronDown, ChevronRight, Users, Award } from 'lucide-react';
import { useTheme } from './shared';
import { S, getLang } from './strings';
import * as api from './api';

function fmtMoney(x) {
  const n = Number(x || 0);
  const lang = getLang();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  return n.toLocaleString(locale, { maximumFractionDigits: 2 });
}

function fmtDate(x) {
  if (!x) return '';
  const lang = getLang();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  try {
    return new Date(x).toLocaleDateString(locale);
  } catch (e) {
    return '';
  }
}

function localizeType(type) {
  const s = S().referral;
  switch (type) {
    case 'REFERRAL_REWARD': return s.typeReferralReward;
    case 'TRANSFER_IN': return s.typeTransferIn;
    case 'TRANSFER_OUT': return s.typeTransferOut;
    case 'SPEND': return s.typeSpend;
    case 'ADMIN_ADJUST': return s.typeAdminAdjust;
    default: return type;
  }
}

export default function ReferralTab() {
  const t = useTheme();
  const s = S().referral;

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [copiedToast, setCopiedToast] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // transfer modal
  const [showTransfer, setShowTransfer] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNote, setTransferNote] = useState('');
  const [transferError, setTransferError] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [successToast, setSuccessToast] = useState(null);
  const searchTimerRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [i, h] = await Promise.all([
        api.getReferralInfo(),
        api.getReferralHistory(),
      ]);
      setInfo(i);
      setHistory(Array.isArray(h) ? h : []);
    } catch (e) {
      setError(e && e.message ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Search with debounce
  useEffect(() => {
    if (!showTransfer) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await api.searchReferralUser(query);
        setSearchResults(Array.isArray(res) ? res : []);
      } catch (e) {
        setSearchResults([]);
      }
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [query, showTransfer]);

  const handleCopy = async () => {
    if (!info || !info.referralLink) return;
    try {
      await navigator.clipboard.writeText(info.referralLink);
      setCopiedToast(true);
      setTimeout(() => setCopiedToast(false), 2000);
    } catch (e) {
      // fallback
      try {
        const ta = document.createElement('textarea');
        ta.value = info.referralLink;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopiedToast(true);
        setTimeout(() => setCopiedToast(false), 2000);
      } catch (_) {}
    }
  };

  const openTransfer = () => {
    setShowTransfer(true);
    setQuery('');
    setSearchResults([]);
    setSelectedUser(null);
    setTransferAmount('');
    setTransferNote('');
    setTransferError(null);
  };

  const closeTransfer = () => {
    setShowTransfer(false);
    setTransferError(null);
  };

  const handleTransfer = async () => {
    setTransferError(null);
    const amountNum = Number(transferAmount);
    if (!selectedUser) {
      setTransferError(s.errorNoRecipient);
      return;
    }
    if (!amountNum || amountNum <= 0 || !isFinite(amountNum)) {
      setTransferError(s.errorInvalidAmount);
      return;
    }
    const balance = Number(info?.bonusBalance || 0);
    if (amountNum > balance) {
      setTransferError(s.errorInvalidAmount);
      return;
    }
    try {
      setTransferLoading(true);
      await api.transferBonuses(selectedUser.id, amountNum, transferNote || undefined);
      const login = selectedUser.login || selectedUser.name || selectedUser.email || '';
      setSuccessToast(s.transferSuccess(fmtMoney(amountNum), login));
      setTimeout(() => setSuccessToast(null), 3500);
      closeTransfer();
      await loadAll();
    } catch (e) {
      setTransferError((e && e.message) || s.errorTransferFailed);
    } finally {
      setTransferLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 28, color: t.textMuted, fontSize: 14 }}>
        {S().common?.loading || 'Загрузка...'}
      </div>
    );
  }

  if (error || !info) {
    return (
      <div style={{ padding: 28, color: t.accentRed, fontSize: 14 }}>
        {error || 'Error'}
      </div>
    );
  }

  const balanceNum = Number(info.bonusBalance || 0);
  const canTransfer = balanceNum > 0;

  const cardStyle = {
    background: t.bgCard,
    border: `1.5px solid ${t.border}`,
    borderRadius: 14,
    padding: 22,
    boxShadow: t.shadow,
  };

  return (
    <div style={{ padding: 28, maxWidth: 1100, margin: '0 auto', color: t.text }}>
      {/* Page header */}
      <div style={{ marginBottom: 26 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Gift size={24} color={t.accent} />
          {s.title}
        </h1>
        <div style={{ fontSize: 14, color: t.textMuted, marginTop: 6 }}>{s.subtitle}</div>
      </div>

      {/* Section A: Link + Balance */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 24 }}>
        <div style={cardStyle}>
          <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 10, fontWeight: 600 }}>{s.myLink}</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              readOnly
              value={info.referralLink || ''}
              style={{
                flex: 1,
                background: t.bgInput,
                border: `1px solid ${t.border}`,
                borderRadius: 8,
                padding: '10px 14px',
                color: t.text,
                fontSize: 14,
                fontFamily: 'JetBrains Mono, monospace',
              }}
            />
            <button
              onClick={handleCopy}
              style={{
                background: t.accent,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <Copy size={16} />
              {copiedToast ? s.copied : s.copyLink}
            </button>
          </div>
          <div style={{ marginTop: 14, fontSize: 13, color: t.textMuted }}>
            {s.myCode}: <span style={{ color: t.text, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1 }}>{info.referralCode}</span>
          </div>
        </div>

        <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 8, fontWeight: 600 }}>{s.balance}</div>
          <div style={{ fontSize: 32, fontWeight: 800, color: t.accent }}>
            {fmtMoney(balanceNum)} {s.balanceUnit}
          </div>
        </div>
      </div>

      {/* Section B0: Sub-user notice (shown only for non-owners) */}
      {info.isOrgOwner === false && (
        <div style={{
          ...cardStyle,
          marginBottom: 24,
          borderLeft: `4px solid ${t.accent}`,
          background: t.bgCard,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: t.accent, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Gift size={18} />
            {s.subUserNoticeTitle}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: t.textMuted }}>
            {s.subUserNoticeText}
          </div>
        </div>
      )}

      {/* Section B: How it works */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>{s.howItWorks}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[s.rule1, s.rule2, s.rule3, s.rule4].map((rule, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: t.accent,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0,
              }}>{idx + 1}</div>
              <div style={{ fontSize: 14, color: t.text, paddingTop: 4 }}>{rule}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section C: Invited friends */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={18} color={t.accent} />
              {s.invitedTitle}
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>
              {s.invitedTotal(info.totalInvited || 0)} · {s.invitedRewarded(info.totalRewarded || 0)}
            </div>
          </div>
          {canTransfer && (
            <button
              onClick={openTransfer}
              style={{
                background: 'transparent',
                color: t.accent,
                border: `1.5px solid ${t.accent}`,
                borderRadius: 8,
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Send size={14} />
              {s.transferButton}
            </button>
          )}
        </div>

        {(!info.referrals || info.referrals.length === 0) ? (
          <div style={{ color: t.textMuted, textAlign: 'center', padding: 30, fontSize: 14 }}>
            {s.invitedEmpty}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${t.border}`, color: t.textMuted, textAlign: 'left' }}>
                  <th style={{ padding: '10px 8px', fontWeight: 600 }}>{s.colDate}</th>
                  <th style={{ padding: '10px 8px', fontWeight: 600 }}>{s.colLogin}</th>
                  <th style={{ padding: '10px 8px', fontWeight: 600 }}>{s.colStatus}</th>
                </tr>
              </thead>
              <tbody>
                {info.referrals.map((r) => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                    <td style={{ padding: '12px 8px', color: t.textMuted }}>{fmtDate(r.joinedAt)}</td>
                    <td style={{ padding: '12px 8px', color: t.text, fontWeight: 600 }}>{r.login || '-'}</td>
                    <td style={{ padding: '12px 8px' }}>
                      {r.rewarded ? (
                        <span style={{
                          background: t.accentGreen + '22',
                          color: t.accentGreen,
                          padding: '4px 10px',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                        }}>{s.statusRewarded} ({fmtMoney(r.rewardAmount || 0)} ₸)</span>
                      ) : (
                        <span style={{
                          background: t.bgInput,
                          color: t.textMuted,
                          padding: '4px 10px',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                        }}>{s.statusPending}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Section D: History collapsible */}
      <div style={cardStyle}>
        <button
          onClick={() => setHistoryOpen(v => !v)}
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            color: t.text,
            padding: 0,
          }}
        >
          {historyOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <Award size={18} color={t.accent} />
          <span style={{ fontSize: 16, fontWeight: 700 }}>{s.historyTitle}</span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: t.textMuted }}>({history.length})</span>
        </button>

        {historyOpen && (
          <div style={{ marginTop: 16 }}>
            {history.length === 0 ? (
              <div style={{ color: t.textMuted, textAlign: 'center', padding: 20, fontSize: 14 }}>
                {s.historyEmpty}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {history.map((item) => {
                  const amt = Number(item.amount || 0);
                  const isPositive = amt > 0;
                  return (
                    <div key={item.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 12px',
                      background: t.bgInput,
                      borderRadius: 8,
                      fontSize: 13,
                    }}>
                      <div style={{ color: t.textMuted, fontSize: 11, minWidth: 90 }}>{fmtDate(item.createdAt)}</div>
                      <div style={{ flex: 1, color: t.text, fontWeight: 600 }}>{localizeType(item.type)}</div>
                      {item.note && (
                        <div style={{ color: t.textMuted, fontStyle: 'italic', fontSize: 12, flex: 2 }}>{item.note}</div>
                      )}
                      <div style={{
                        color: isPositive ? t.accentGreen : t.accentRed,
                        fontWeight: 700,
                        minWidth: 100,
                        textAlign: 'right',
                      }}>
                        {isPositive ? '+' : ''}{fmtMoney(amt)} ₸
                      </div>
                      <div style={{ color: t.textMuted, fontSize: 11, minWidth: 110, textAlign: 'right' }}>
                        = {fmtMoney(item.balanceAfter)} ₸
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Success toast */}
      {successToast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: t.accentGreen,
          color: '#fff',
          padding: '14px 20px',
          borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          fontSize: 14,
          fontWeight: 600,
          zIndex: 1000,
        }}>
          {successToast}
        </div>
      )}

      {/* Transfer modal */}
      {showTransfer && (
        <div
          onClick={closeTransfer}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: t.bgCard,
              border: `1.5px solid ${t.border}`,
              borderRadius: 14,
              padding: 26,
              width: 480,
              maxWidth: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text }}>{s.transferTitle}</div>
              <button
                onClick={closeTransfer}
                style={{ background: t.bgInput, border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: t.textMuted }}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 16 }}>{s.transferHint}</div>

            {/* Recipient search */}
            {!selectedUser ? (
              <div style={{ marginBottom: 14 }}>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={s.transferSearch}
                  style={{
                    width: '100%',
                    background: t.bgInput,
                    border: `1px solid ${t.border}`,
                    borderRadius: 8,
                    padding: '10px 14px',
                    color: t.text,
                    fontSize: 14,
                  }}
                />
                {query.length >= 3 && (
                  <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', border: `1px solid ${t.border}`, borderRadius: 8 }}>
                    {searchResults.length === 0 ? (
                      <div style={{ padding: 12, color: t.textMuted, fontSize: 13, textAlign: 'center' }}>
                        {s.transferNoResults}
                      </div>
                    ) : (
                      searchResults.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => { setSelectedUser(u); setQuery(''); setSearchResults([]); }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: `1px solid ${t.border}`,
                            fontSize: 13,
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = t.bgInput}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ fontWeight: 600, color: t.text }}>{u.login || u.name}</div>
                          <div style={{ color: t.textMuted, fontSize: 11 }}>{u.email}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                background: t.bgInput,
                borderRadius: 8,
                marginBottom: 14,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: t.text, fontSize: 14 }}>{selectedUser.login || selectedUser.name}</div>
                  <div style={{ color: t.textMuted, fontSize: 12 }}>{selectedUser.email}</div>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted }}
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {/* Amount */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6, fontWeight: 600 }}>{s.transferAmount}</div>
              <input
                type="number"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                min="1"
                max={balanceNum}
                style={{
                  width: '100%',
                  background: t.bgInput,
                  border: `1px solid ${t.border}`,
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: t.text,
                  fontSize: 14,
                }}
              />
              <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                {s.balance}: {fmtMoney(balanceNum)} ₸
              </div>
            </div>

            {/* Note */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 6, fontWeight: 600 }}>{s.transferNote}</div>
              <textarea
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  background: t.bgInput,
                  border: `1px solid ${t.border}`,
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: t.text,
                  fontSize: 14,
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {transferError && (
              <div style={{ color: t.accentRed, fontSize: 13, marginBottom: 12 }}>{transferError}</div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={closeTransfer}
                disabled={transferLoading}
                style={{
                  background: 'transparent',
                  color: t.textMuted,
                  border: `1px solid ${t.border}`,
                  borderRadius: 8,
                  padding: '10px 18px',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {s.transferCancel}
              </button>
              <button
                onClick={handleTransfer}
                disabled={transferLoading}
                style={{
                  background: t.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 18px',
                  cursor: transferLoading ? 'wait' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  opacity: transferLoading ? 0.6 : 1,
                }}
              >
                {s.transferSend}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
